import { useQueries } from '@tanstack/react-query';
import { BUSINESS, CATEGORY, COST_DASHBOARD_VARIANT } from 'pages/admin/CostDashboard/constants';
import { getIntervalFromDateRange } from 'pages/admin/CostDashboard/helper';
import { bff } from 'shared/api';
import {
  AreaType,
  BusinessCostSummaryReply,
  BusinessCostSummaryRequest,
} from 'shared/types/protoc-gen/bffbillsystem';
import { QUERY_KEYS } from 'shared/types/react-query';

export type AllBusinessCostSummaryFilter = Pick<
  BusinessCostSummaryRequest,
  'namespace' | 'startTime' | 'endTime'
>;

interface IUseGetAllBusinessSummaries extends AllBusinessCostSummaryFilter {
  variant: COST_DASHBOARD_VARIANT;
  area: AreaType;
}

export interface BusinessCostSummaryData {
  businessKey: string;
  data: BusinessCostSummaryReply | undefined;
}

const useGetAllBusinessSummaries = ({
  area,
  endTime,
  namespace,
  startTime,
  variant,
}: IUseGetAllBusinessSummaries) => {
  const businessKeys = Object.values(
    variant === COST_DASHBOARD_VARIANT.ROLLUP ? BUSINESS : CATEGORY,
  );

  const queries = useQueries({
    queries: businessKeys.map(business => ({
      queryKey: [
        QUERY_KEYS.ADMIN_ALL_BUSINESS_COST_SUMMARIES,
        startTime,
        endTime,
        namespace,
        business,
        area,
      ],
      queryFn: async (): Promise<BusinessCostSummaryReply> => {
        const res = await bff.get('/bff/cost/business_summary', {
          params: {
            namespace: namespace !== 'ALL' ? namespace : '',
            startTime,
            endTime,
            business,
            area,
            interval: getIntervalFromDateRange(startTime, endTime),
          },
        });

        return res.data;
      },
    })),
  });

  const isLoading = queries.some(q => q.isLoading);
  const isError = queries.some(q => q.isError);
  const isSuccess = queries.every(q => q.isSuccess);

  const data: BusinessCostSummaryData[] = businessKeys.map((businessKey, index) => ({
    businessKey,
    data: queries[index]?.data,
  }));

  return {
    data,
    isLoading,
    isError,
    isSuccess,
  };
};

export default useGetAllBusinessSummaries;
